import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getPeriods } from '../../../../api/periods/get-periods'
import { getProducts } from '../../../../api/products/get-products'
import { OlTeflPriceInput } from '../../../../lib/types/models/ol/tefl-price/ol-tefl-price-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<OlTeflPriceInput> & {
  mode: 'create' | 'update'
  className?: string
  defaultValues?: OlTeflPriceInput
}

const formSchema: ZodType<OlTeflPriceInput> = z.object({
  productIds: createNumberArraySchema('produits', { isInteger: true, minimumNumberOfItems: 1 }),
  periodIds: createNumberArraySchema('périodes', { isInteger: true, minimumNumberOfItems: 1 }),
  activityOnlyPrice: createNumberSchema('prix des activités', { isPositive: true, nullish: true }),
  fullDayPrice: createNumberSchema('prix de la journée complète', {
    isPositive: true,
    nullish: true
  }),
  tuitionAndActivityPrice: createNumberSchema('prix des cours et activités', {
    isPositive: true,
    nullish: true
  }),
  tuitionOnlyPrice: createNumberSchema('prix des cours uniquement', {
    isPositive: true,
    nullish: true
  })
})

const OlTeflPriceForm: React.FC<Props> = ({
  mode,
  className,
  defaultValues,
  ...validationButtonProps
}) => {
  const form = useForm<OlTeflPriceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className="rounded-2xl">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            {mode === 'create' ? 'Ajouter un prix TEFL' : 'Modifier un prix TEFL'}
          </Typography>
          <div className="h-5" />
          <div className={cn('flex flex-col gap-3', className)}>
            <FormField
              control={form.control}
              name={'productIds'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      label="Produits"
                      variant="primary"
                      searchable
                      options={
                        products?.data.map((product) => ({
                          label: product.name,
                          value: product.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((productId) => ({
                          label:
                            products?.data.find((product) => product.id === productId)?.name ?? '',
                          value: productId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'periodIds'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      required
                      label="Période(s)"
                      variant="primary"
                      searchable
                      options={
                        periods?.data.map((period) => ({
                          label: period.name,
                          value: period.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((periodId) => ({
                          label:
                            periods?.data.find((period) => period.id === periodId)?.name ?? '',
                          value: periodId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 justify-items-start place-items-baseline place-self-start gap-3 gap-x-0">
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                Tuition and Activity (€)
              </Typography>
              <FormField
                control={form.control}
                name={'tuitionAndActivityPrice'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput {...field} type="number" label="Prix par jour" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                Tuition Only (€)
              </Typography>
              <FormField
                control={form.control}
                name={'tuitionOnlyPrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} type="number" label="Prix par jour" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                Activity Only (€)
              </Typography>
              <FormField
                control={form.control}
                name={'activityOnlyPrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} type="number" label="Prix par jour" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
                Full Day Outing (€)
              </Typography>
              <FormField
                control={form.control}
                name={'fullDayPrice'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} type="number" label="Prix par jour" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default OlTeflPriceForm
